/**
 * Macro calculator using Mifflin-St Jeor equation.
 *
 * Inputs: weight (lbs), height (feet + inches), age, sex, trainingDaysPerWeek, goal, targetWeight
 * Outputs: { daily: {...macros}, maintenance, bmr, goal }
 *
 * Design principles:
 *  - Deficit/surplus expressed as % of TDEE so it scales with body size
 *  - Deficit scales down as you approach target (aggressive early, gentle near goal)
 *  - Calorie floor at BMR — never starve the metabolism
 *  - Protein driven by goal, not a flat rate:
 *      cut  → 1g/lb target weight (preserve LBM, not excess calories)
 *      gain → 0.85g/lb current weight (support hypertrophy without crowding carbs)
 *      maint → 0.8g/lb current weight
 *  - Fat at 25% of calories (hormonal health + fat-soluble vitamins), floor at 0.35g/lb
 *  - Carbs fill whatever calories remain
 */

export function calculateMacros({
  weight,            // lbs
  heightFeet,
  heightInches,
  age,
  sex,               // "Male" | "Female"
  trainingDaysPerWeek,
  goal,              // "lose_fat" | "build_muscle" | "get_stronger" | "feel_better" | "train_event"
  targetWeight,      // lbs (optional)
}) {
  // ── Inputs ────────────────────────────────────────────────────────────────
  const weightLbs = parseFloat(weight) || 170;
  const weightKg  = weightLbs * 0.453592;
  const heightCm  =
    (parseFloat(heightFeet)   || 5) * 30.48 +
    (parseFloat(heightInches) || 0) * 2.54;
  const ageNum = parseFloat(age) || 30;

  const tgtLbs      = targetWeight ? parseFloat(targetWeight) : null;
  const lbsToTarget = tgtLbs ? Math.abs(weightLbs - tgtLbs) : 0;
  const isCutting   = tgtLbs && weightLbs > tgtLbs && lbsToTarget > 2;
  const isGaining   = tgtLbs && weightLbs < tgtLbs && lbsToTarget > 2;

  // ── BMR (Mifflin-St Jeor) ─────────────────────────────────────────────────
  const bmr = sex === "Female"
    ? 10 * weightKg + 6.25 * heightCm - 5 * ageNum - 161
    : 10 * weightKg + 6.25 * heightCm - 5 * ageNum + 5;

  // ── TDEE ──────────────────────────────────────────────────────────────────
  const days = parseFloat(trainingDaysPerWeek) || 4;
  const activityFactor =
    days <= 2 ? 1.375 :
    days <= 3 ? 1.50  :
    days <= 4 ? 1.55  :
    days <= 5 ? 1.70  :
                1.80;

  const maintenance = Math.round(bmr * activityFactor);

  // ── Calorie target ────────────────────────────────────────────────────────
  // Deficit/surplus as % of TDEE, graduated by distance to target.
  let calories;

  if (goal === "lose_fat" || isCutting) {
    // 12–20% deficit; eases off as you close in on goal
    const deficitPct =
      lbsToTarget > 30 ? 0.20 :
      lbsToTarget > 15 ? 0.18 :
      lbsToTarget > 5  ? 0.15 :
      tgtLbs           ? 0.12 :   // within 5 lbs of target — very gentle
                         0.18;    // no target set → moderate default
    calories = Math.round(maintenance * (1 - deficitPct));

  } else if (goal === "build_muscle" || goal === "get_stronger" || isGaining) {
    // 8–12% lean surplus; smaller as you near target to avoid excess fat gain
    const surplusPct =
      lbsToTarget > 20 ? 0.12 :
      lbsToTarget > 5  ? 0.10 :
      tgtLbs           ? 0.08 :
                         0.10;
    calories = Math.round(maintenance * (1 + surplusPct));

  } else {
    // feel_better / train_event — eat at maintenance
    calories = maintenance;
  }

  // Hard floor: never eat below BMR (protects metabolism)
  calories = Math.max(calories, Math.round(bmr));

  // ── Protein ───────────────────────────────────────────────────────────────
  let proteinG;
  if (isCutting && tgtLbs) {
    // 1g/lb target weight: high enough to preserve muscle while cutting,
    // low enough not to waste calories that could be carbs
    proteinG = Math.round(tgtLbs * 1.0);
  } else if (goal === "lose_fat" && !tgtLbs) {
    // Bug fix: lose_fat without a target weight previously fell through to the
    // maintenance rate (0.8g/lb). Cutting requires higher protein to preserve
    // lean mass, so treat it the same as isCutting but anchored to current weight.
    proteinG = Math.round(weightLbs * 1.0);
  } else if (isGaining || goal === "build_muscle" || goal === "get_stronger") {
    // 0.85g/lb: above minimum for growth, leaves room for carbs
    proteinG = Math.round(weightLbs * 0.85);
  } else {
    // Maintenance / general health: 0.8g/lb
    proteinG = Math.round(weightLbs * 0.8);
  }
  // Floor: 0.7g/lb current weight regardless of goal (anti-catabolism minimum)
  proteinG = Math.max(proteinG, Math.round(weightLbs * 0.7));

  // ── Fat ───────────────────────────────────────────────────────────────────
  // 25% of total calories (supports hormones and fat-soluble vitamin absorption)
  // Floor: 0.35g/lb (minimum for hormonal health independent of calorie level)
  const fatFloor = Math.round(weightLbs * 0.35);
  let fatG = Math.max(Math.round((calories * 0.25) / 9), fatFloor);

  // For lean cutters, high protein + 25% fat can exceed the calorie target,
  // which previously zeroed carbs AND left the recomputed total above target.
  // Trim discretionary fat toward its floor first so the total tracks target.
  if (proteinG * 4 + fatG * 9 > calories) {
    fatG = Math.max(fatFloor, Math.round((calories - proteinG * 4) / 9));
  }

  // ── Carbs ─────────────────────────────────────────────────────────────────
  // Remaining calories after protein and fat
  const carbsG = Math.round(Math.max(0, calories - proteinG * 4 - fatG * 9) / 4);

  // ── Fiber ─────────────────────────────────────────────────────────────────
  // 14g per 1,000 kcal (Dietary Reference Intake)
  const fiberG = Math.round(calories / 1000) * 14;

  // Recalculate calories from actual macro grams to keep display consistent
  const daily = {
    calories: proteinG * 4 + carbsG * 4 + fatG * 9,
    protein:  proteinG,
    carbs:    carbsG,
    fat:      fatG,
    fiber:    fiberG,
  };

  return {
    daily,
    maintenance,
    bmr: Math.round(bmr),
    goal,
  };
}

/**
 * Given macro_goals from profile, return today's targets.
 * Handles both new flat format and legacy { training_day, rest_day } format.
 */
export function getTodayMacros(macroGoals) {
  if (!macroGoals) return null;
  // Legacy format: pick training_day as the representative value
  if (macroGoals.training_day) return macroGoals.training_day;
  return macroGoals;
}
